import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  chartThemeTokens,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Spinner,
  ToggleGroup,
  ToggleGroupItem
} from 'design-system';

import type { TimeRangeButton } from '@/config/constants';
import { DEFAULT_CHART_HEIGHT, TABLE_ROW_HEIGHT_PX } from '@/config/constants';
import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import { chartOptions } from '@/features/charts';
import ChartShell from '@/features/charts/ChartShell';
import useCsvDownload from '@/features/charts/hooks/useCsvDownload';
import usePrefersReducedMotion from '@/features/charts/hooks/usePrefersReducedMotion';
import useRegularChart from '@/features/charts/RegularChart/useRegularChart';
import { createChartAppearance } from '@/features/charts/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/features/charts/types/highcharts';
import { deepMerge } from '@/shared/lib/array/deepMerge';
import { detectEnvironment } from '@/shared/lib/detectEnvironment';

import type Highcharts from 'highcharts/highstock';

const environment = detectEnvironment();
const GROUP_NAME = `Meta_Go_Config${environment === 'staging' ? '_Staging' : ''}`;
const regularAppearance = createChartAppearance(chartThemeTokens, 'regular');

type LayoutProps = {
  fullWidth?: boolean;
  height?: number;
};

type CustomChartProps = {
  metadata?: Record<string, object | string>;
  layout?: LayoutProps;
};

const resolveSankeyTooltipFormatter = (tooltip?: Highcharts.TooltipOptions) => {
  if (!tooltip?.formatter) return;
  if (typeof tooltip.formatter === 'function') return;

  const rawFormatter = tooltip.formatter as unknown;
  if (typeof rawFormatter === 'string') {
    const formatterKey = rawFormatter.trim();
    if (formatterKey && !formatterKey.includes('function') && !formatterKey.includes('=>')) {
      tooltip.pointFormat = tooltip.pointFormat ?? formatterKey;
      delete tooltip.formatter;
      return;
    }
  }

  delete tooltip.formatter;
};

const getDefaultRange = (timeRangeButtons: TimeRangeButton[]) =>
  timeRangeButtons.find(button => button.default)?.value ?? 'all';

const ChartUnavailableAlert = ({ partial = false }: { partial?: boolean }) => (
  <Alert>
    <AlertTitle>
      {partial ? 'Some chart data may be unavailable' : 'Chart data unavailable'}
    </AlertTitle>
    <AlertDescription>
      {partial
        ? 'Available series are shown below.'
        : "We couldn't retrieve the requested chart data. Please try again later."}
    </AlertDescription>
  </Alert>
);

const NoDataState = () => (
  <Empty className='min-h-72'>
    <EmptyHeader>
      <EmptyTitle>No chart data</EmptyTitle>
      <EmptyDescription>
        No data available for the selected date range. Try adjusting it.
      </EmptyDescription>
    </EmptyHeader>
  </Empty>
);

const Index = ({ metadata, layout }: CustomChartProps) => {
  const { hasPermission } = usePermission();
  const prefersReducedMotion = usePrefersReducedMotion();

  const seriesFileName = metadata?.seriesFileName as string;
  const configFileName = metadata?.configFileName as string;
  const queryFileId = metadata?.queryFileId as string;
  const downloadFileId = metadata?.downloadFileId as string;
  const label = typeof metadata?.label === 'string' ? metadata.label : 'Custom chart';

  const timeRangeButtons = useMemo(
    () => (metadata?.timeRangeButtons as TimeRangeButton[] | undefined) ?? [],
    [metadata]
  );
  const [from, setFrom] = useState(() => getDefaultRange(timeRangeButtons));
  const selectedRangeLabel = timeRangeButtons.find(button => button.value === from)?.label;
  const { height } = layout || {};

  const {
    data: chartConfig,
    isError: isChartConfigError,
    isLoading: isChartConfigLoading
  } = useQuery(chartOptions.customConfig(configFileName, GROUP_NAME));
  const {
    data: chartSeriesData,
    isError: isChartSeriesError,
    isLoading: isChartSeriesLoading
  } = useQuery(
    chartOptions.customData({
      fileName: seriesFileName,
      groupName: GROUP_NAME,
      queryFileId,
      from
    })
  );
  const chartSeries = chartSeriesData as ExtendedSeriesOptionsType[] | undefined;
  useCsvDownload(from, downloadFileId);

  const { chartContainerRef } = useRegularChart({ chartSeries });
  const resolvedChartOptions = useMemo<ExtendedOptions | null>(() => {
    if (!chartConfig || !chartSeries) return null;

    const chartHeight = height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT;
    const baseXAxis: Highcharts.XAxisOptions = {
      labels: { style: { color: regularAppearance.foreground } },
      gridLineColor: regularAppearance.grid,
      lineColor: regularAppearance.grid,
      tickColor: regularAppearance.grid
    };
    const baseYAxis: Highcharts.YAxisOptions = {
      title: { text: '' },
      labels: { style: { color: regularAppearance.foreground } },
      gridLineColor: regularAppearance.grid
    };

    const baseOptions: ExtendedOptions = {
      accessibility: { enabled: false },
      title: { text: undefined },
      colors: [...regularAppearance.palette],
      exporting: {
        buttons: { contextButton: { enabled: false } },
        scale: 1,
        enabled: true,
        filename: 'GX-Chart',
        chartOptions: {
          legend: {
            enabled: true,
            itemStyle: {
              color: regularAppearance.foreground,
              fontSize: '0.8em',
              textOverflow: 'ellipsis'
            }
          },
          scrollbar: { enabled: false }
        }
      },
      tooltip: { enabled: false },
      stockTools: { gui: { enabled: false } },
      legend: { enabled: true, itemStyle: { color: regularAppearance.foreground } },
      credits: { enabled: false },
      xAxis: baseXAxis,
      yAxis: baseYAxis,
      series: chartSeries,
      chart: {
        reflow: true,
        backgroundColor: regularAppearance.background,
        height: chartHeight,
        style: {
          color: regularAppearance.foreground,
          fontFamily: regularAppearance.fontFamily
        }
      }
    };

    const merged = deepMerge(baseOptions, chartConfig);

    if (Array.isArray(merged.xAxis)) {
      merged.xAxis = merged.xAxis.map(axis => deepMerge(structuredClone(baseXAxis), axis));
    }
    if (Array.isArray(merged.yAxis)) {
      merged.yAxis = merged.yAxis.map(axis => deepMerge(structuredClone(baseYAxis), axis));
    }
    if (merged.chart?.type === 'sankey') {
      resolveSankeyTooltipFormatter(merged.tooltip);
    }
    if (prefersReducedMotion) {
      merged.chart = { ...merged.chart, animation: false };
      merged.plotOptions = {
        ...merged.plotOptions,
        series: { ...merged.plotOptions?.series, animation: false }
      };
      merged.tooltip = { ...merged.tooltip, animation: false };
    }

    return {
      ...merged,
      exporting: merged.exporting
        ? {
            pdfFont: {
              normal: 'https://www.highcharts.com/samples/data/fonts/NotoSans-Regular.ttf'
            },
            ...merged.exporting,
            chartOptions: {
              ...merged.exporting.chartOptions,
              chart: {
                ...merged.exporting.chartOptions?.chart,
                events: merged.exporting.chartOptions?.chart?.events
              }
            }
          }
        : merged.exporting
    };
  }, [chartConfig, chartSeries, height, prefersReducedMotion]);

  const hasAnyData =
    Array.isArray(chartSeries) &&
    chartSeries.some(series => Array.isArray(series?.data) && series.data.length > 0);
  const hasQueryError = isChartSeriesError || isChartConfigError;
  const totalSeries = chartSeries?.length ?? 0;
  const visibleSeries = chartSeries?.filter(series => series.visible !== false).length ?? 0;

  if (!hasPermission(Permission.ViewMarketScreens)) {
    return (
      <section
        aria-label={label}
        className='flex min-h-50 items-center justify-center'
      >
        <Alert className='max-w-md'>
          <AlertTitle>Access restricted</AlertTitle>
          <AlertDescription>
            You no longer have access. Please upgrade to access the full chart.
          </AlertDescription>
        </Alert>
      </section>
    );
  }

  if (isChartSeriesLoading || isChartConfigLoading) {
    return (
      <section
        aria-label={label}
        className='flex items-center justify-center'
        style={{ height: DEFAULT_CHART_HEIGHT }}
      >
        <Spinner
          aria-label='Loading chart data'
          className='size-8'
        />
      </section>
    );
  }

  return (
    <section
      ref={chartContainerRef}
      aria-label={label}
      className='w-full min-w-0 overflow-x-hidden'
    >
      {timeRangeButtons.length > 0 && (
        <header className='mb-2 flex flex-wrap items-center justify-end gap-2'>
          <ToggleGroup
            aria-label='Chart range'
            spacing={0}
            value={from ? [from] : []}
            variant='outline'
            onValueChange={values => {
              const value = values.at(-1);
              if (value && value !== from) setFrom(value);
            }}
          >
            {timeRangeButtons.map(button => (
              <ToggleGroupItem
                key={button.value}
                aria-label={button.label}
                value={button.value}
              >
                {button.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </header>
      )}
      <div className='base-chart space-y-3'>
        {hasQueryError && <ChartUnavailableAlert partial={hasAnyData} />}
        {hasAnyData && resolvedChartOptions && (
          <ChartShell
            isStockChart={false}
            options={resolvedChartOptions}
            summary={{
              kind: 'line',
              label,
              ...(selectedRangeLabel ? { rangeLabel: selectedRangeLabel } : {}),
              totalSeries,
              visibleSeries
            }}
          />
        )}
        {!hasAnyData && <NoDataState />}
      </div>
    </section>
  );
};

export default Index;
